import { SVButton, SVForm, SVTextInput } from '@/components/core';
import { MESSAGES } from '@/constants/messages';
import { useAddPendingActions } from '@/hooks/requests/useBankApplications';
import { useAuthUser } from '@/hooks/stores/useAuthUser';
import {
  BankApplicationDetails,
  PendingActionsFormFields,
} from '@/types/bankApplications';
import showToast from '@/utils/toast';
import { Add, Delete } from '@mui/icons-material';
import {
  Box,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

type BankAccountApplicationPendingActionsProps = {
  bankApplication: BankApplicationDetails;
};

export default function BankAccountApplicationPendingActions({
  bankApplication,
}: BankAccountApplicationPendingActionsProps) {
  const { uuid, pendingActions, business, primaryOwner } = bankApplication;
  const defaultBusinessActions = pendingActions.find(
    a => a.businessId !== null,
  );
  const defaultOwnerActions = pendingActions.find(a => a.ownerId !== null);

  const [open, setOpen] = useState(false);
  const { isStaff } = useAuthUser();
  const formMethods = useForm<PendingActionsFormFields>({
    defaultValues: {
      id: uuid,
      businessId: business?.uuid,
      businessActions: defaultBusinessActions
        ? defaultBusinessActions.actions.map(a => {
            return { value: a };
          })
        : [],
      ownerId: primaryOwner?.consumer.uuid,
      ownerActions: defaultOwnerActions
        ? defaultOwnerActions.actions.map(a => {
            return { value: a };
          })
        : [],
    },
  });
  const { control } = formMethods;

  const {
    fields: businessFields,
    append: businessAppend,
    remove: businessRemove,
  } = useFieldArray<PendingActionsFormFields>({
    control,
    name: 'businessActions' as const,
  });

  const {
    fields: ownerFields,
    append: ownerAppend,
    remove: ownerRemove,
  } = useFieldArray<PendingActionsFormFields>({
    control,
    name: 'ownerActions' as const,
  });

  const { addPendingActions, isAddPendingActionsPending } =
    useAddPendingActions();

  async function handleSubmit(data: PendingActionsFormFields) {
    try {
      await addPendingActions(data);
      showToast({
        type: 'success',
        data: {
          content: 'Pendings actions have been successfully updated',
        },
      });
      setOpen(false);
    } catch {
      showToast({
        type: 'error',
        data: {
          content: MESSAGES.genericError,
        },
      });
    }
  }

  return (
    <>
      <Card>
        <Typography variant="h5" mb={2}>
          Pending Actions
        </Typography>
        <Stack spacing={2}>
          {pendingActions.length === 0 && (
            <Typography>
              This bank account application has no pending actions.
            </Typography>
          )}
          {pendingActions.map(pending => {
            if (!pending.actions.length) {
              return null;
            }

            return (
              <Box key={pending.actions.join(',')}>
                <Typography>
                  <Typography component="span" fontWeight="bold">
                    {pending.businessId
                      ? `${business?.name}`
                      : primaryOwner
                        ? `${primaryOwner.consumer.firstName}`
                        : 'N/A'}
                  </Typography>
                  {' - '}
                  {pending.businessId ? 'Business' : 'Control Person'}
                </Typography>
                {pending.actions.map((action, index) => (
                  <Card key={action}>
                    <Typography>
                      {index + 1}. {action}
                    </Typography>
                  </Card>
                ))}
              </Box>
            );
          })}
        </Stack>
        {isStaff && (
          <SVButton onClick={() => setOpen(true)} sx={{ mt: 2 }}>
            {pendingActions.length ? 'Edit' : 'Add'} Pending Actions
          </SVButton>
        )}
      </Card>
      <Dialog
        fullWidth
        maxWidth="lg"
        open={open}
        onClose={() => setOpen(false)}>
        <SVForm methods={formMethods} onSubmit={() => {}}>
          <DialogTitle>Pending Actions</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <Typography>
                Add up to 5 pending actions for each owner or business
              </Typography>
              {business && (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 2,
                    }}>
                    <Typography variant="h6">{business.name}</Typography>
                    {businessFields.length < 5 && (
                      <SVButton
                        variant="text"
                        startIcon={<Add />}
                        onClick={() => businessAppend({ value: '' })}>
                        Add
                      </SVButton>
                    )}
                  </Box>
                  {businessFields.map((item, index) => (
                    <Grid key={item.id} container spacing={2}>
                      <Grid size={{ xs: 12, md: 11 }}>
                        <SVTextInput
                          name={`businessActions.${index}.value`}
                          label="Pending business action"
                          rules={{ required: true }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 1 }}>
                        <IconButton onClick={() => businessRemove(index)}>
                          <Delete color="error" />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}
                </>
              )}
              {primaryOwner && (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 2,
                    }}>
                    <Typography variant="h6">
                      {primaryOwner.consumer.fullName}
                    </Typography>
                    {ownerFields.length < 5 && (
                      <SVButton
                        variant="text"
                        startIcon={<Add />}
                        onClick={() => ownerAppend({ value: '' })}>
                        Add
                      </SVButton>
                    )}
                  </Box>
                  {ownerFields.map((item, index) => (
                    <Grid key={item.id} container spacing={2}>
                      <Grid size={{ xs: 12, md: 11 }}>
                        <SVTextInput
                          name={`ownerActions.${index}.value`}
                          label="Pending owner action"
                          rules={{ required: true }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 1 }}>
                        <IconButton onClick={() => ownerRemove(index)}>
                          <Delete color="error" />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}
                </>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <SVButton variant="tertiary" onClick={() => setOpen(false)}>
              Cancel
            </SVButton>
            <SVButton
              isLoading={isAddPendingActionsPending}
              onClick={formMethods.handleSubmit(handleSubmit)}>
              Confirm
            </SVButton>
          </DialogActions>
        </SVForm>
      </Dialog>
    </>
  );
}
